import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  url?: string;
  type?: string;
  image?: string;
  article?: {
    publishedTime?: string;
    tags?: string[];
  };
}

const SITE_NAME = 'NaldaDev';
const DEFAULT_DESCRIPTION = 'NaldaDev는 고객님의 비전을 함께 성장시킬 수 있는 개발 파트너입니다. React, Vue, Node.js 등 최신 기술로 비즈니스 성공을 도와드립니다.';
const SITE_URL = 'https://nalda.dev';

export default function SEO({ title, description = DEFAULT_DESCRIPTION, url, type = 'website', image, article }: SEOProps) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | 비전을 코드로 구현하는 개발 파트너`;
  const pageUrl = url ? `${SITE_URL}${url}` : SITE_URL;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      {image && <meta property="og:image" content={`${SITE_URL}${image}`} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={`${SITE_URL}${image}`} />}

      {/* Article metadata */}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.tags?.map(tag => <meta key={tag} property="article:tag" content={tag} />)}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': type === 'article' ? 'Article' : 'Organization',
          ...(type === 'article'
            ? {
                headline: title,
                description,
                datePublished: article?.publishedTime,
                author: { '@type': 'Organization', name: SITE_NAME },
                publisher: { '@type': 'Organization', name: SITE_NAME },
              }
            : {
                name: SITE_NAME,
                url: SITE_URL,
                description: DEFAULT_DESCRIPTION,
                contactPoint: {
                  '@type': 'ContactPoint',
                  email: 'contact@nalda.dev',
                  contactType: 'customer service',
                },
              }),
        })}
      </script>
    </Helmet>
  );
}
